import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clamp01 } from './stormParams';
import { composeIntensity } from './intensity';

// The intensity engine + scene-change conductor. Lives above <Routes> so its state and
// timers survive navigation (react-router unmounts routes instantly with no exit hook).

export type Pulse = { kind: 'tear' | 'surge' | 'warp'; id: number; strength: number };

interface StormState {
  intensity: number;
  restingFloor: number;
  setRestingFloor: (n: number) => void;
  reducedMotion: boolean;
  setScroll: (n: number) => void;
  setWords: (n: number) => void;
  setRhythm: (n: number) => void;
  setDepth: (n: number) => void; // archive: how far into the past (0..1)
  pulse: Pulse | null;
  tear: (opts?: { to?: string; strength?: number }) => void;
  surge: (strength: number) => Promise<void>;
  warp: () => void;
}

const StormCtx = createContext<StormState | null>(null);
const DEFAULT_FLOOR = 0.22;
const TEAR_SWAP_MS = 180; // navigate under cover of the tear
const TEAR_TOTAL_MS = 520;
const SURGE_MS = 700;
const WARP_MS = 180;

function readFloorFromUrl(fallback: number): number {
  try {
    const v = new URLSearchParams(window.location.search).get('storm');
    if (v == null) return fallback;
    const n = parseFloat(v);
    return Number.isFinite(n) ? clamp01(n) : fallback;
  } catch {
    return fallback;
  }
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  });
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export const StormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const reducedMotion = usePrefersReducedMotion();
  const [restingFloor, setFloorRaw] = useState(() => readFloorFromUrl(DEFAULT_FLOOR));
  const [scroll, setScrollRaw] = useState(0);
  const [words, setWordsRaw] = useState(0);
  const [rhythm, setRhythmRaw] = useState(0);
  const [depth, setDepthRaw] = useState(0);
  const [pulse, setPulse] = useState<Pulse | null>(null);
  const timers = useRef<number[]>([]);
  const pulseId = useRef(0);

  useEffect(() => () => { timers.current.forEach((t) => window.clearTimeout(t)); }, []);

  const later = useCallback((ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  const fire = useCallback((kind: Pulse['kind'], strength: number, ms: number) => {
    const id = ++pulseId.current;
    setPulse({ kind, id, strength: clamp01(strength) });
    later(ms, () => setPulse((p) => (p && p.id === id ? null : p)));
  }, [later]);

  const tear = useCallback((opts?: { to?: string; strength?: number }) => {
    if (reducedMotion) { if (opts?.to) navigate(opts.to); return; }
    fire('tear', opts?.strength ?? 0.8, TEAR_TOTAL_MS);
    if (opts?.to) { const to = opts.to; later(TEAR_SWAP_MS, () => navigate(to)); }
  }, [reducedMotion, fire, later, navigate]);

  const surge = useCallback((strength: number) => new Promise<void>((resolve) => {
    if (reducedMotion) { resolve(); return; }
    fire('surge', strength, SURGE_MS);
    later(SURGE_MS, resolve);
  }), [reducedMotion, fire, later]);

  const warp = useCallback(() => {
    if (reducedMotion) return;
    fire('warp', 0.5, WARP_MS);
  }, [reducedMotion, fire]);

  const base = composeIntensity({ floor: restingFloor, scroll: Math.max(scroll, depth), words, rhythm });
  const intensity = reducedMotion ? 0 : pulse?.kind === 'surge' ? Math.max(base, pulse.strength) : base;

  // Setters must be referentially stable: consumers use them in effect deps/cleanups
  // (e.g. GuidedJournal resets scroll on unmount), so a new identity per render would
  // fire those cleanups on every intensity change.
  const setRestingFloor = useCallback((n: number) => setFloorRaw(clamp01(n)), []);
  const setScroll = useCallback((n: number) => setScrollRaw(clamp01(n)), []);
  const setWords = useCallback((n: number) => setWordsRaw(Math.max(0, Number.isFinite(n) ? n : 0)), []);
  const setRhythm = useCallback((n: number) => setRhythmRaw(clamp01(n)), []);
  const setDepth = useCallback((n: number) => setDepthRaw(clamp01(n)), []);

  const value = useMemo<StormState>(() => ({
    intensity, restingFloor, reducedMotion, pulse, tear, surge, warp,
    setRestingFloor, setScroll, setWords, setRhythm, setDepth,
  }), [intensity, restingFloor, reducedMotion, pulse, tear, surge, warp,
    setRestingFloor, setScroll, setWords, setRhythm, setDepth]);

  return <StormCtx.Provider value={value}>{children}</StormCtx.Provider>;
};

export function useStorm(): StormState {
  const ctx = useContext(StormCtx);
  if (!ctx) throw new Error('useStorm must be used within <StormProvider>');
  return ctx;
}
